import {
  FocalEventPosterImage,
  getCoverEventPosterImage,
} from './FocalEventPosterImage';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { trackImageLoadFailed } from '@/lib/analytics/posthog';
import { DESIGN_TOKENS } from '@/lib/design/theme';
import { cn } from '@/lib/utils';
import { type DiscoveryEvent as RadiousEvent } from '@/lib/api/apiTypes';
import { formatEventDayLabel, formatEventTime, getEventPalette } from '@/features/events/eventFormatting';
import { LinearGradient } from 'expo-linear-gradient';
import { ImageOffIcon } from 'lucide-react-native';
import * as React from 'react';
import { View } from 'react-native';

export function EventPoster({
  event,
  className,
  showMeta = true,
}: {
  event: RadiousEvent;
  className?: string;
  showMeta?: boolean;
}) {
  const [base, accent] = getEventPalette(event.id);
  const posterImage = React.useMemo(() => getCoverEventPosterImage(event), [event]);
  const posterURL = posterImage?.public_url ?? null;
  const [failedPosterURL, setFailedPosterURL] = React.useState<string | null>(null);
  const showPoster = Boolean(posterURL) && failedPosterURL !== posterURL;

  if (showPoster && posterImage) {
    return (
      <View
        className={cn('overflow-hidden border', className)}
        style={{
          backgroundColor: DESIGN_TOKENS.colors.surface,
          borderColor: DESIGN_TOKENS.colors.border,
        }}>
        <FocalEventPosterImage
          image={posterImage}
          className="h-full w-full"
          onError={() => {
            trackImageLoadFailed('event');
            setFailedPosterURL(posterURL);
          }}
        />
        <LinearGradient
          colors={['transparent', 'rgba(8,6,4,0.4)']}
          pointerEvents="none"
          style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 40 }}
        />
        {showMeta ? (
          <View
            className="absolute left-3 top-3 rounded-full border px-3 py-1.5"
            style={{
              borderColor: DESIGN_TOKENS.colors.borderStrong,
              backgroundColor: 'rgba(26, 23, 16, 0.82)',
            }}>
            <Text variant="small" className="uppercase tracking-[1.6px] text-white">
              {formatEventDayLabel(event.starts_at)}
            </Text>
          </View>
        ) : null}
      </View>
    );
  }

  return (
    <View
      className={cn('overflow-hidden border', className)}
      style={{
        backgroundColor: DESIGN_TOKENS.colors.surface,
        borderColor: DESIGN_TOKENS.colors.border,
      }}>
      <View style={{ flex: 1, backgroundColor: base }}>
        <View className="absolute inset-0 items-center justify-center">
          <Icon as={ImageOffIcon} size={32} className="text-white/35" />
        </View>
        <View
          style={{
            position: 'absolute',
            right: -18,
            top: 18,
            width: 110,
            height: 110,
            borderRadius: 999,
            backgroundColor: accent,
            opacity: 0.78,
          }}
        />
        <View
          style={{
            position: 'absolute',
            left: -28,
            bottom: -32,
            width: 140,
            height: 140,
            borderRadius: 999,
            backgroundColor: accent,
            opacity: 0.22,
          }}
        />
        <View
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(8, 8, 8, 0.18)',
          }}
        />
        <View className="flex-1 justify-between p-4">
          <View>
            <View
              className="self-start rounded-full border px-3 py-1.5"
              style={{
                borderColor: DESIGN_TOKENS.colors.borderStrong,
                backgroundColor: 'rgba(17, 16, 8, 0.58)',
              }}>
              <Text variant="small" className="uppercase tracking-[1.6px] text-white">
                {formatEventDayLabel(event.starts_at)}
              </Text>
            </View>
          </View>
          <View>
            <Text variant="large" className="text-xl font-semibold text-white" numberOfLines={3}>
              {event.title}
            </Text>
            {showMeta ? (
              <Text variant="small" className="mt-2 font-medium text-white/80">
                {event.venue_name} • {formatEventTime(event.starts_at)}
              </Text>
            ) : null}
          </View>
        </View>
      </View>
    </View>
  );
}
